/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2023, Bulb Inc. For more information, please email contact@hellobulb.com.
*/

import {
  Component, Input, OnInit,
} from '@angular/core';
import confetti from 'canvas-confetti';
import { ErrorService } from 'src/common/error.service';
import { ButtonSize, ButtonStyle } from 'src/aero/aero-button/aero-button.component';
import { AssignmentSubmitUploadHttpService } from '../http.service';
import { ShareTransition, UnitStateStore } from '../../../unit/unit-state.store';

/**
 * Component for the submit/ unsubmit button on assignments.
 */
@Component({
  selector: 'bulb-submit-assignment-button',
  templateUrl: './submit-assignment-button.component.html',
  styleUrls: ['./submit-assignment-button.component.scss'],
  standalone: false,
})
export class SubmitAssignmentButtonComponent implements OnInit {

  @Input() isShareDisabled: boolean;

  @Input() unit: any;

  @Input() styleClass: string;

  public buttonStyle: ButtonStyle = ButtonStyle.PRIMARY_2;

  @Input() buttonSize: ButtonSize = ButtonSize.SLIM;

  public isSubmitted: boolean;

  constructor(
    private httpClient: AssignmentSubmitUploadHttpService,
    private errorService: ErrorService,
    private unitStateStore: UnitStateStore,
  ) { }

  ngOnInit() {
    this.isSubmitted = this.unit.assignmentSubmitted;
    this.unitStateStore.shareStatus$.subscribe((shareStatus) => {
      this.isSubmitted = shareStatus === 'private' ? false : this.isSubmitted;
    });
  }

  /**
   * Submits the current assigment.
   */
  submitAssignment() {
    this.httpClient.submit(this.unit.id)
      .then(() => {
        this.unitStateStore.transitionShareStatus(ShareTransition.SHARE);
        this.isSubmitted = true;
        SubmitAssignmentButtonComponent.emitConfetti();
      })
      .catch((error) => {
        this.errorService.captureErrorMessage(error, `Error submitting assignment. Unit Id: ${this.unit.id}`);
      });
  }

  /**
   * Unsubmits the current assigment.
   */
  unsubmitAssignment() {
    this.httpClient.unsubmitAssignment(this.unit.id)
      .then(() => {
        this.isSubmitted = false;
      })
      .catch((error) => {
        this.errorService.captureErrorMessage(error, `Error unsubmitting assignment. Unit Id: ${this.unit.id}`);
      });
  }

  /**
   * Either submits or unsubmits an assignment based of its current status.
   */
  toggleSubmit() {
    if (!this.isSubmitted) {
      this.submitAssignment();
    } else {
      this.unsubmitAssignment();
    }
  }

  /**
   * Emits a fun confetti effect when a user submits an assignment.
   */
  private static emitConfetti() {
    // These are our bulb accent colors. See: _variables.scss - `$bulb-accent-colors`.
    const colors = ['#ed1847', '#f26522', '#faa61a', '#fff200', '#00bfdf', '#00aeef'];
    const count = 800;
    const defaults = {
      origin: { y: 0.7 },
      colors,
    };

    function fire(particleRatio, opts) {
      confetti({ ...defaults, ...opts, particleCount: Math.floor(count * particleRatio) });
    }

    fire(0.25, {
      spread: 26,
      startVelocity: 55,
    });
    fire(0.2, {
      spread: 60,
    });
    fire(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8,
    });
    fire(0.1, {
      spread: 120,
      startVelocity: 25,
      decay: 0.92,
      scalar: 1.2,
    });
    fire(0.1, {
      spread: 120,
      startVelocity: 45,
    });
  }
}
